import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../services/api';
import './AdminVoteCounting.css';

const AdminVoteCounting = () => {
  const [constituencies, setConstituencies] = useState([]);
  const [selectedConstituency, setSelectedConstituency] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [results, setResults] = useState([]);
  const [notaData, setNotaData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [roundNo, setRoundNo] = useState(1);
  const [isFinal, setIsFinal] = useState(false);
  const [message, setMessage] = useState(null);
  const [notaVotes, setNotaVotes] = useState(0);
  const [editMode, setEditMode] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    loadConstituencies();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadConstituencies = async () => {
    try {
      const response = await api.getVotingConstituencies();
      if (response.success) {
        const sortedConstituencies = response.data.sort((a, b) => {
          const numA = parseInt(a.code.replace(/\D/g, ''), 10);
          const numB = parseInt(b.code.replace(/\D/g, ''), 10);
          return numA - numB;
        });
        setConstituencies(sortedConstituencies);
      }
    } catch (error) {
      console.error('Error loading constituencies:', error);
    }
  };

  const handleConstituencyChange = async (constituencyId, constituencyName) => {
    setSelectedConstituency(constituencyId);
    setSearchQuery(constituencyName);
    setShowDropdown(false);
    setLoading(true);

    try {
      const [candidatesRes, resultsRes] = await Promise.all([
        api.getCandidatesForVoting(constituencyId),
        api.getConstituencyResults(constituencyId)
      ]);

      if (candidatesRes.success) {
        const candidatesWithVotes = candidatesRes.data.map(candidate => ({
          ...candidate,
          currentVotes: 0
        }));
        setCandidates(candidatesWithVotes);
      }

      if (resultsRes.success) {
        setResults(resultsRes.data);
        setNotaData(resultsRes.nota || { totalVotes: 0, rounds: [] });
        const maxRound = Math.max(
          ...(resultsRes.data[0]?.rounds?.map(r => r.roundNo) || [0]),
          ...(resultsRes.nota?.rounds?.map(r => r.roundNo) || [0])
        );
        setRoundNo(maxRound + 1);
        
        // Check if any round is marked as final
        const hasFinalRound = resultsRes.data.some(r => 
          r.rounds?.some(round => round.isFinal === true)
        );
        setIsFinal(hasFinalRound);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      setMessage({ type: 'error', text: 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  };

  const handleRoundChange = (newRoundNo) => {
    setRoundNo(newRoundNo);

    if (!results.length) return;

    const roundExists = results.some(r =>
      r.rounds?.some(round => round.roundNo === newRoundNo)
    );

    if (roundExists) {
      setEditMode(true);
      setCandidates(prev => prev.map(candidate => {
        const candidateResult = results.find(r => r.candidateId === candidate.id);
        const roundData = candidateResult?.rounds?.find(r => r.roundNo === newRoundNo);
        return {
          ...candidate,
          currentVotes: roundData?.votes || 0
        };
      }));

      const notaRound = notaData?.rounds?.find(r => r.roundNo === newRoundNo);
      setNotaVotes(notaRound?.votes || 0);
      
      // Check if the selected round is marked as final
      const selectedRoundIsFinal = results.some(r => 
        r.rounds?.some(round => round.roundNo === newRoundNo && round.isFinal === true)
      );
      setIsFinal(selectedRoundIsFinal);
      
      setMessage({ type: 'info', text: `Editing Round ${newRoundNo}` });
    } else {
      setEditMode(false);
      setCandidates(prev => prev.map(c => ({ ...c, currentVotes: 0 })));
      setNotaVotes(0);
      setIsFinal(false);
      setMessage(null);
    }
  };

  const handleVoteChange = (candidateId, votes) => {
    setCandidates(prev =>
      prev.map(c =>
        c.id === candidateId ? { ...c, currentVotes: parseInt(votes) || 0 } : c
      )
    );
  };

  const handleSubmit = async () => {
    if (!selectedConstituency) {
      setMessage({ type: 'error', text: 'Please select a constituency' });
      return;
    }

    const updates = candidates.filter(c => c.currentVotes > 0);

    if (updates.length === 0 && notaVotes === 0) {
      setMessage({ type: 'error', text: 'Please enter votes for at least one candidate or NOTA' });
      return;
    }

    setLoading(true);

    try {
      const response = await api.bulkUpdateVotes({
        constituencyId: selectedConstituency,
        roundNo,
        isFinal,
        notaVotes: notaVotes > 0 ? notaVotes : null,
        updates: updates.map(c => ({
          candidateId: c.id,
          votes: c.currentVotes
        }))
      });

      if (response.success) {
        setMessage({
          type: 'success',
          text: editMode ? `Round ${roundNo} updated successfully!` : `Round ${roundNo} added successfully!`
        });

        const resultsRes = await api.getConstituencyResults(selectedConstituency);
        if (resultsRes.success) {
          setResults(resultsRes.data);
          setNotaData(resultsRes.nota || { totalVotes: 0, rounds: [] });
        }

        setCandidates(prev => prev.map(c => ({ ...c, currentVotes: 0 })));
        setNotaVotes(0);
        setEditMode(false);
        setIsFinal(false);
        setRoundNo(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error updating votes:', error);
      setMessage({ type: 'error', text: 'Failed to update votes' });
    } finally {
      setLoading(false);
    }
  };

  const filteredConstituencies = constituencies.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.code.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedCandidates = [...candidates].sort((a, b) => {
    const aResult = results.find(r => r.candidateId === a.id);
    const bResult = results.find(r => r.candidateId === b.id);
    const aVotes = aResult?.totalVotes || 0;
    const bVotes = bResult?.totalVotes || 0;
    return bVotes - aVotes;
  });

  const getImageUrl = (photoUrl) => {
    if (!photoUrl) return null;
    if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) {
      return photoUrl;
    }
    const cleanPath = photoUrl.startsWith('/') ? photoUrl.substring(1) : photoUrl;
    const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://localhost:7014';
    return `${baseUrl}/${cleanPath}`;
  };

  // Check if there's a final round to display winner
  const hasFinalResults = results.some(r => 
    r.rounds?.some(round => round.isFinal === true)
  );

  return (
    <div className="admin-vote-counting-page">
      <div className="vote-counting-header">
        <h1>
          <i className="fas fa-vote-yea"></i>
          Vote Counting & Results
        </h1>
        <p>Update live counting results for constituencies</p>
      </div>

      {message && (
        <div className={`alert alert-${message.type}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check-circle' : message.type === 'info' ? 'info-circle' : 'exclamation-circle'}`}></i>
          {message.text}
          <button onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="counting-controls">
        <div className="control-group searchable-dropdown" ref={dropdownRef}>
          <label>Select Constituency</label>

          <div className="search-input-wrapper">
            <input
              type="text"
              className="constituency-search"
              placeholder="🔍 Search by code or name..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
            />
            {searchQuery && (
              <button
                className="clear-search"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedConstituency(null);
                  setCandidates([]);
                  setResults([]);
                  setNotaData(null);
                  setNotaVotes(0);
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>

          {showDropdown && (
            <div className="dropdown-list">
              {filteredConstituencies.length > 0 ? (
                filteredConstituencies.map(c => (
                  <div
                    key={c.id}
                    className={`dropdown-item ${selectedConstituency === c.id ? 'selected' : ''}`}
                    onClick={() => handleConstituencyChange(c.id, `${c.code} - ${c.name}`)}
                  >
                    <span className="constituency-code">{c.code}</span>
                    <span className="constituency-name">{c.name}</span>
                  </div>
                ))
              ) : (
                <div className="dropdown-item no-results">
                  <i className="fas fa-search"></i>
                  No constituencies found
                </div>
              )}
            </div>
          )}
        </div>

        <div className="control-group">
          <label>Round Number</label>
          <input
            type="number"
            value={roundNo}
            onChange={(e) => handleRoundChange(parseInt(e.target.value) || 1)}
            min="1"
          />
          {editMode && <small style={{ color: '#f59e0b' }}>✏️ Editing existing round</small>}
        </div>

        <div className="control-group checkbox">
          <label>
            <input
              type="checkbox"
              checked={isFinal}
              onChange={(e) => setIsFinal(e.target.checked)}
            />
            Mark as Final Results
          </label>
        </div>
      </div>

      {selectedConstituency && (
        <>
          <div className="candidates-voting-table">
            <h3>
              {editMode ? `Edit Round ${roundNo} Votes` : `Enter Votes for Round ${roundNo}`}
            </h3>
            <table>
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Candidate</th>
                  <th>Party</th>
                  <th>Latest Round Votes</th>
                  <th>Round {roundNo} Votes</th>
                </tr>
              </thead>
              <tbody>
                {sortedCandidates.map((candidate, index) => {
                  const candidateResult = results.find(r => r.candidateId === candidate.id);
                  const latestRound = candidateResult?.rounds?.length
                    ? candidateResult.rounds[candidateResult.rounds.length - 1]
                    : null;
                  const latestRoundVotes = latestRound?.votes || 0;
                  const latestRoundNo = latestRound?.roundNo || '-';

                  const imageUrl = getImageUrl(candidate.photoUrl);

                  return (
                    <tr key={candidate.id} className={index === 0 && latestRoundVotes > 0 ? 'top-candidate' : ''}>
                      <td className="rank-cell">
                        {latestRoundVotes > 0 ? (
                          <span className={`rank-badge rank-${index + 1}`}>
                            #{index + 1}
                          </span>
                        ) : (
                          <span className="rank-badge no-votes">-</span>
                        )}
                      </td>
                      <td className="candidate-info">
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={candidate.name}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className="candidate-avatar-fallback" style={{ display: imageUrl ? 'none' : 'flex' }}>
                          <i className="fas fa-user"></i>
                        </div>
                        <span>{candidate.name}</span>
                      </td>
                      <td>
                        <span className="party-badge">{candidate.partyShortName}</span>
                      </td>
                      <td className="total-votes">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span style={{ fontSize: '18px', fontWeight: '700' }}>
                            {latestRoundVotes.toLocaleString()}
                          </span>
                          {latestRound && (
                            <small style={{ color: '#64748b', fontSize: '11px' }}>
                              Round {latestRoundNo}
                            </small>
                          )}
                        </div>
                        {index === 0 && latestRoundVotes > 0 && (
                          <span className="leading-badge">LATEST</span>
                        )}
                      </td>
                      <td>
                        <input
                          type="number"
                          value={candidate.currentVotes}
                          onChange={(e) => handleVoteChange(candidate.id, e.target.value)}
                          placeholder="0"
                          min="0"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="nota-row">
                  <td className="rank-cell">
                    <span className="rank-badge nota-badge">
                      <i className="fas fa-ban"></i>
                    </span>
                  </td>
                  <td className="candidate-info">
                    <div className="nota-icon">
                      <i className="fas fa-times-circle"></i>
                    </div>
                    <span className="nota-text">NOTA (None Of The Above)</span>
                  </td>
                  <td>
                    <span className="party-badge nota-badge-party">NOTA</span>
                  </td>
                  <td className="total-votes">
                    {(() => {
                      const notaLatestRound = notaData?.rounds?.length
                        ? notaData.rounds[notaData.rounds.length - 1]
                        : null;
                      const notaLatestVotes = notaLatestRound?.votes || 0;
                      const notaLatestRoundNo = notaLatestRound?.roundNo || '-';
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span className="nota-total" style={{ fontSize: '18px', fontWeight: '700' }}>
                            {notaLatestVotes.toLocaleString()}
                          </span>
                          {notaLatestRound && (
                            <small style={{ color: '#64748b', fontSize: '11px' }}>
                              Round {notaLatestRoundNo}
                            </small>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td>
                    <input
                      type="number"
                      value={notaVotes}
                      onChange={(e) => setNotaVotes(parseInt(e.target.value) || 0)}
                      placeholder="0"
                      min="0"
                      className="nota-input"
                    />
                  </td>
                </tr>
              </tfoot>
            </table>
            <div className="submit-section">
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="btn-submit-votes"
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    {editMode ? 'Updating...' : 'Submitting...'}
                  </>
                ) : (
                  <>
                    <i className={`fas fa-${editMode ? 'edit' : 'check'}`}></i>
                    {editMode ? `Update Round ${roundNo}` : `Submit Round ${roundNo}`}
                  </>
                )}
              </button>
            </div>
          </div>
          {results.length > 0 && (
            <div className="results-summary">
              <h3>Current Standings (Total Votes)</h3>
              <div className="results-grid">
                {results
                  .map(result => {
                    const latestRound = result.rounds && result.rounds.length
                      ? result.rounds[result.rounds.length - 1]
                      : null;
                    const latestVote = latestRound?.votes ?? 0;
                    return {
                      ...result,
                      latestVote
                    };
                  })
                  .sort((a, b) => b.latestVote - a.latestVote)
                  .map((result, index) => (
                    <div
                      key={result.candidateId}
                      className={`result-card rank-${index + 1}${index === 0 && hasFinalResults ? ' winner-card' : ''}`}
                      style={index === 0 && hasFinalResults ? {
                        border: "3px solid #ffd700",
                        boxShadow: "0 8px 28px rgba(255, 215, 0, 0.18)",
                        position: "relative",
                        overflow: "visible"
                      } : {}}
                    >
                      {index === 0 && hasFinalResults && (
                        <div className="winner-badge"
                          style={{
                            position: "absolute",
                            top: "-18px",
                            right: "-16px",
                            background: "linear-gradient(94deg, #ffe066, #ffd700 85%)",
                            color: "#d18616",
                            fontWeight: "900",
                            fontSize: "17px",
                            padding: "6px 22px 6px 18px",
                            borderRadius: "30px 25px 28px 20px",
                            boxShadow: "0 6px 18px rgba(255, 215, 0, 0.13), 0 0 0 2.5px #fff",
                            display: "flex",
                            alignItems: "center",
                            zIndex: 22,
                            border: "2.5px solid #fff",
                            letterSpacing: "0.5px"
                          }}>
                          <i className="fas fa-crown" style={{ marginRight: "7px", color: "#ff9800", fontSize: "19px" }}></i>
                          WINNER
                        </div>
                      )}
                      <div className="rank">#{index + 1}</div>
                      <div className="candidate-name">{result.candidateName}</div>
                      <div className="party-name">{result.partyShortName}</div>
                      <div
                        className="vote-count"
                        style={index === 0 && hasFinalResults ? {
                          background: "linear-gradient(135deg, #ffd947 0%, #ffa800 100%)",
                          color: "#8a6c11",
                          borderRadius: "12px",
                          fontWeight: "800",
                          fontSize: "20px",
                          display: "inline-block",
                          padding: "3px 20px",
                          margin: "0 auto 6px auto",
                          boxShadow: "0 2px 12px rgba(255, 215, 0, 0.08)"
                        } : {}}
                      >
                        {(result.latestVote || 0).toLocaleString()} votes
                      </div>
                      {result.rounds && result.rounds.length > 0 && (
                        <div style={{ fontSize: "12px", color: "#888" }}>
                          Round {result.rounds[result.rounds.length - 1]?.roundNo}
                        </div>
                      )}
                    </div>
                  ))}
                {notaData && notaData.rounds && notaData.rounds.length > 0 && (() => {
                  const notaLatestRound = notaData.rounds[notaData.rounds.length - 1];
                  return (
                    <div className="result-card result-card-nota">
                      <div className="rank">
                        <i className="fas fa-ban"></i>
                      </div>
                      <div className="candidate-name">NOTA</div>
                      <div className="party-name">None Of The Above</div>
                      <div className="vote-count">{(notaLatestRound?.votes || 0).toLocaleString()} votes</div>
                      <div style={{ fontSize: '12px', color: '#888' }}>
                        Round {notaLatestRound?.roundNo}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminVoteCounting;
